import React, { useEffect, useState } from "react";
import { Trash2, AlertTriangle, RefreshCw } from "lucide-react";
import { listClientErrors, deleteClientError, clearClientErrors } from "../../lib/api";
import { toast } from "sonner";

export function ClientErrorsSection() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(new Set());

    const reload = async () => {
        setLoading(true);
        try {
            const r = await listClientErrors();
            setRows(r || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load client errors");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
    }, []);

    const toggle = (id) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleDelete = async (id) => {
        try {
            await deleteClientError(id);
            setRows((prev) => prev.filter((r) => r.id !== id));
            toast.success("Error deleted");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const handleClear = async () => {
        if (!window.confirm(`Clear all ${rows.length} client errors?`)) return;
        try {
            const res = await clearClientErrors();
            toast.success(`Cleared ${res.deleted || 0} errors`);
            setRows([]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Clear failed");
        }
    };

    if (loading) {
        return (
            <div className="text-sm text-slate-500" data-testid="admin-client-errors-loading">
                Loading client errors…
            </div>
        );
    }

    return (
        <div data-testid="admin-client-errors-section">
            <div className="flex items-center gap-2 mb-3">
                <div className="text-xs text-slate-400">
                    <span className="font-bold text-white" data-testid="admin-client-errors-count">
                        {rows.length}
                    </span>{" "}
                    error{rows.length === 1 ? "" : "s"}
                    {rows.length >= 500 ? " (500-row cap)" : ""}
                </div>
                <button
                    type="button"
                    onClick={reload}
                    data-testid="admin-client-errors-refresh"
                    className="flex items-center gap-1 px-2 py-1 text-xs rounded-sm border border-[#2A3655] text-slate-300 hover:text-white hover:border-[#00A3FF]/60"
                >
                    <RefreshCw className="w-3 h-3" />
                    Refresh
                </button>
                {rows.length > 0 && (
                    <button
                        type="button"
                        onClick={handleClear}
                        data-testid="admin-client-errors-clear"
                        className="ml-auto flex items-center gap-1 px-2 py-1 text-xs rounded-sm border border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                    >
                        <Trash2 className="w-3 h-3" />
                        Clear all
                    </button>
                )}
            </div>

            {rows.length === 0 ? (
                <div className="text-sm text-slate-500 border border-dashed border-[#2A3655] rounded-sm p-6 text-center">
                    No client errors reported. Nothing crashing in the field right now.
                </div>
            ) : (
                <div className="space-y-2">
                    {rows.map((r) => {
                        const isOpen = expanded.has(r.id);
                        const when = r.created_at
                            ? new Date(r.created_at).toLocaleString()
                            : "";
                        return (
                            <div
                                key={r.id}
                                data-testid={`admin-client-error-${r.id}`}
                                className="border border-[#2A3655] rounded-sm bg-[#0F1B3A] overflow-hidden"
                            >
                                <button
                                    type="button"
                                    onClick={() => toggle(r.id)}
                                    className="w-full flex items-start gap-3 p-3 text-left hover:bg-[#131A2F] transition-colors"
                                >
                                    <AlertTriangle className="w-4 h-4 text-rose-300 shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                                            <span className="text-rose-300 font-bold uppercase tracking-wider">
                                                {r.label || "error"}
                                            </span>
                                            <span>·</span>
                                            <span>{when}</span>
                                            {r.user_email && (
                                                <>
                                                    <span>·</span>
                                                    <span className="truncate">
                                                        {r.user_email}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                        <div className="text-sm text-white font-mono break-words">
                                            {r.message || "(no message)"}
                                        </div>
                                        {r.url && (
                                            <div className="text-[11px] text-slate-500 mt-1 truncate">
                                                {r.url}
                                            </div>
                                        )}
                                    </div>
                                    <span className="text-[10px] text-slate-500 shrink-0">
                                        {isOpen ? "▲" : "▼"}
                                    </span>
                                </button>
                                {isOpen && (
                                    <div className="px-3 pb-3 border-t border-[#2A3655]">
                                        {r.stack && (
                                            <ErrSection label="Stack">
                                                <pre className="text-[11px] font-mono whitespace-pre-wrap break-words text-slate-300 bg-[#0A1128] p-2 rounded-sm border border-[#1C253C]">
                                                    {r.stack}
                                                </pre>
                                            </ErrSection>
                                        )}
                                        {r.component_stack && (
                                            <ErrSection label="Component stack">
                                                <pre className="text-[11px] font-mono whitespace-pre-wrap break-words text-slate-300 bg-[#0A1128] p-2 rounded-sm border border-[#1C253C]">
                                                    {r.component_stack}
                                                </pre>
                                            </ErrSection>
                                        )}
                                        {r.user_agent && (
                                            <ErrSection label="User agent">
                                                <div className="text-[11px] font-mono text-slate-400">
                                                    {r.user_agent}
                                                </div>
                                            </ErrSection>
                                        )}
                                        <div className="mt-3 flex justify-end">
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(r.id)}
                                                data-testid={`admin-client-error-delete-${r.id}`}
                                                className="flex items-center gap-1 px-2 py-1 text-xs rounded-sm text-rose-300 hover:bg-rose-500/10"
                                            >
                                                <Trash2 className="w-3 h-3" />
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export function ErrSection({ label, children }) {
    return (
        <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                {label}
            </div>
            {children}
        </div>
    );
}


/**
 * iter-79: Superadmin-editable prompt that runs ONLY when one or more
 * pricing templates are attached to the AI estimate. Lets admins tune
 * how strict / flexible the AI is when matching plan items against
 * the user's templates.
 */
